import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, updateDoc, getDoc, serverTimestamp, deleteField, collection, query, where, getDocs, writeBatch, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, COLLECTIONS, upsertUsersRootProfile } from '../../../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { User, Teacher, School } from '../../../utils/firebase';
import { SUBJECTS, SubjectType, getSubjectId, getSubjectName } from '../../../utils/subjectUtils';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import InputField from '../../../components/ui/InputField';
import DropdownButton from '../../../components/ui/DropdownButton';
import ToggleSwitch from '../../../components/ui/ToggleSwitch';
import { resolveTeacherPlan } from '../../../config/teacherPlans';
import { SCHOOL_REGISTRATION_FORM_URL } from '../../../utils/externalLinks';

interface ClassSetting {
  grade: number;
  class: number;
  isActive: boolean;
}

interface FormData {
  name: string;
  subject: string;
  homeroomClass?: {
    grade: number;
    class: number;
    isWordGameActive?: boolean;
    wordGameSettings?: {
      dailyWordLimit: number;
      enablePoints: boolean;
      dailyLimit: boolean;
    };
  };
}

interface SchoolInfo {
  schoolLevel: '초등학교' | '중학교' | '고등학교';
  gradeClasses: Record<number, number>;
  name?: string;
}

const pxToRem = (value: number) => `${value / 16}rem`;

/** 날짜 문자열을 HTML5 date input 형식(YYYY-MM-DD)으로 변환 */
const toDateInputValue = (value: string): string => {
  if (!value || typeof value !== 'string') return '';
  const normalized = value.trim().replace(/\./g, '-');
  const match = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) {
    const [, y, m, d] = match;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const shortMatch = normalized.match(/^(\d{1,2})-(\d{1,2})$/);
  if (shortMatch) {
    const [, m, d] = shortMatch;
    const y = new Date().getFullYear();
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return value;
};

const ACTIVE_SCHOOL_STORAGE_KEY = 'maru_active_school_id';

const getStorageActiveSchoolId = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_SCHOOL_STORAGE_KEY);
  } catch {
    return null;
  }
};

const TeacherSetting: React.FC = () => {
  const { currentUser, userRole, deleteAccount, setCurrentUser } = useAuth();
  const navigate = useNavigate();
  const teacher = currentUser as Teacher;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [homeroomClass, setHomeroomClass] = useState<{ grade: number; class: number }>({ grade: 0, class: 0 });
  const [stampClasses, setStampClasses] = useState<ClassSetting[]>([]);
  const [stampImageUrl, setStampImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subject, setSubject] = useState('');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    subject: '',
    homeroomClass: undefined
  });
  const [selectedClasses, setSelectedClasses] = useState<Record<string, boolean>>({});
  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo>({
    schoolLevel: '초등학교',
    gradeClasses: {}
  });
  const [activeTab, setActiveTab] = useState<'profile' | 'homeroom' | 'subject' | 'stamp'>('profile');
  const [schools, setSchools] = useState<School[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [selectedSchoolForChange, setSelectedSchoolForChange] = useState<string>('');
  const [changingSchool, setChangingSchool] = useState(false);
  const [semesterDates, setSemesterDates] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: '',
    endDate: ''
  });
  /** 포인트 등수 기준: balance = 보유 포인트(벌어든 것 - 쓴 것), totalEarned = 총 벌어든 포인트만 */
  const [pointRankingMode, setPointRankingMode] = useState<'balance' | 'totalEarned'>('balance');

  useEffect(() => {
    const fetchTeacherData = async () => {
      if (!teacher?.uid || !teacher?.schoolId) return;

      try {
        const teacherRef = doc(db, `schools/${teacher.schoolId}/teachers/${teacher.uid}`);
        const teacherDoc = await getDoc(teacherRef);
        
        if (teacherDoc.exists()) {
          const data = teacherDoc.data();
          if (data.homeroomClass) {
            setHomeroomClass(data.homeroomClass);
          }
          if (data.stampClasses) {
            setStampClasses(data.stampClasses);
          }
          if (data.stampImageUrl) {
            setStampImageUrl(data.stampImageUrl);
          }
          if (data.subject) {
            // subjectId를 한글 교과명으로 변환
            const subjectName = getSubjectName(data.subject);
            setSubject(subjectName);
          }
        }
      } catch (err) {
        console.error('교사 데이터 로딩 중 오류:', err);
        setError('교사 정보를 불러오는데 실패했습니다.');
      }
    };

    fetchTeacherData();
  }, [teacher?.uid, teacher?.schoolId]);

  useEffect(() => {
    if (teacher) {
      setFormData({
        name: teacher.name,
        subject: teacher.subject || '',
        homeroomClass: teacher.homeroomClass
      });
      setStampClasses(teacher.stampClasses || []);
      // teacher.subject가 있으면 한글 교과명으로 변환하여 설정
      if (teacher.subject) {
        const subjectName = getSubjectName(teacher.subject);
        setSubject(subjectName);
      }
    }
  }, [teacher]);

  useEffect(() => {
    const fetchSchoolInfo = async () => {
      if (!teacher?.schoolId) return;
      
      try {
        const schoolRef = doc(db, COLLECTIONS.SCHOOLS, teacher.schoolId);
        const schoolDoc = await getDoc(schoolRef);
        
        if (schoolDoc.exists()) {
          const data = schoolDoc.data();
          const schoolLevelMap: {[key: string]: '초등학교' | '중학교' | '고등학교'} = {
            'elementary': '초등학교',
            'middle': '중학교',
            'high': '고등학교'
          };
          
          setSchoolInfo({
            schoolLevel: schoolLevelMap[data.type] || '초등학교',
            gradeClasses: data.gradeClasses || {},
            name: data.name
          });
        }
      } catch (err) {
        console.error('학교 정보 로딩 중 오류:', err);
        setError('학교 정보를 불러오는데 실패했습니다.');
      }
    };
    
    fetchSchoolInfo();
  }, [teacher?.schoolId]);

  // 학교 목록 가져오기 (소속교 변경용)
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setLoadingSchools(true);
        const schoolsRef = collection(db, COLLECTIONS.SCHOOLS);
        const schoolsSnapshot = await getDocs(schoolsRef);
        const schoolsList = schoolsSnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        })) as School[];
        schoolsList.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        setSchools(schoolsList);
      } catch (err) {
        console.error('학교 목록 로딩 중 오류:', err);
      } finally {
        setLoadingSchools(false);
      }
    };
    fetchSchools();
  }, []);

  // teacher 로드 시 선택된 소속교 초기화
  useEffect(() => {
    if (teacher?.schoolId) {
      setSelectedSchoolForChange(teacher.schoolId);
    }
  }, [teacher?.schoolId]);

  useEffect(() => {
    const fetchSemesterDates = async () => {
      if (!teacher?.schoolId || !teacher?.classId) return;
      
      try {
        const classRef = doc(db, `schools/${teacher.schoolId}/classes/${teacher.classId}`);
        const classDoc = await getDoc(classRef);
        
        if (classDoc.exists()) {
          const classData = classDoc.data();
          if (classData.semesterDates) {
            setSemesterDates(classData.semesterDates);
          }
          if (classData.pointRankingMode === 'totalEarned' || classData.pointRankingMode === 'balance') {
            setPointRankingMode(classData.pointRankingMode);
          }
        }
      } catch (err) {
        console.error('학기 날짜 정보 로딩 중 오류:', err);
      }
    };

    if (teacher?.homeroomClass && teacher?.classId) {
      fetchSemesterDates();
    }
  }, [teacher?.schoolId, teacher?.classId, teacher?.homeroomClass]);

  const getGradeRange = (): number[] => {
    switch (schoolInfo.schoolLevel) {
      case '초등학교':
        return [1, 2, 3, 4, 5, 6];
      case '중학교':
      case '고등학교':
        return [1, 2, 3];
      default:
        return [1, 2, 3, 4, 5, 6];
    }
  };

  const getClassRange = (grade: number): number[] => {
    return Array.from(
      { length: schoolInfo.gradeClasses[grade] || 0 }, 
      (_, i) => i + 1
    );
  };

  const handleHomeroomClassChange = (field: 'grade' | 'class', value: string) => {
    const numValue = parseInt(value);
    setHomeroomClass(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  const handleAddStampClass = () => {
    const newStampClass: ClassSetting = {
      grade: 0,
      class: 0,
      isActive: false
    };
    setStampClasses([...stampClasses, newStampClass]);
  };

  const handleUpdateStampClass = (index: number, field: keyof ClassSetting, value: any) => {
    const updatedStampClasses = [...stampClasses];
    if (field === 'grade' || field === 'class') {
      value = Number(value) || 0;
    } else if (field === 'isActive') {
      value = Boolean(value);
    }
    updatedStampClasses[index] = {
      ...updatedStampClasses[index],
      [field]: value
    };
    setStampClasses(updatedStampClasses);
  };

  const handleRemoveStampClass = (index: number) => {
    setStampClasses(stampClasses.filter((_, i) => i !== index));
  };

  const handleStampImageUpload = async (file: File) => {
    if (resolveTeacherPlan(teacher.plan, teacher.role) === 'free') {
      setError('도장 이미지는 Firebase 저장소에 올라가 요금이 발생합니다. 무료 요금제에서는 올릴 수 없습니다.');
      return;
    }
    try {
      if (!storage) {
        throw new Error('Storage service is not available');
      }
      
      setUploading(true);
      const storageRef = ref(storage, `stamps/${teacher.uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      
      const teacherRef = doc(db, `schools/${teacher.schoolId}/teachers/${teacher.uid}`);
      await updateDoc(teacherRef, {
        stampImageUrl: downloadUrl,
        updatedAt: new Date()
      });

      setStampImageUrl(downloadUrl);
      setSuccess('도장 이미지가 업로드되었습니다.');
    } catch (error) {
      console.error('Error uploading stamp image:', error);
      setError('스탬프 이미지 업로드 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!teacher?.uid || !teacher?.schoolId) return;

    try {
      setLoading(true);
      const teacherDocRef = doc(db, `schools/${teacher.schoolId}/teachers/${teacher.uid}`);
      
      // 업데이트할 데이터 준비
      const updatedData: any = {
        name: formData.name,
        updatedAt: serverTimestamp()
      };

      // subject와 subjectId 업데이트
      const subjectId = subject ? getSubjectId(subject as SubjectType) : null;
      updatedData.subject = subjectId;
      updatedData.subjectId = subjectId;

      // homeroomClass가 있는 경우에만 추가
      if (homeroomClass.grade > 0 && homeroomClass.class > 0) {
        updatedData.homeroomClass = {
          grade: Number(homeroomClass.grade),
          class: Number(homeroomClass.class)
        };
        // classId 업데이트
        updatedData.classId = teacher.schoolId + '_' + homeroomClass.grade + '_' + homeroomClass.class;
      } else {
        updatedData.homeroomClass = null;
        updatedData.classId = '';
      }

      // stampClasses가 있는 경우에만 추가
      if (stampClasses.length > 0) {
        updatedData.stampClasses = stampClasses.map(stampClass => ({
          grade: Number(stampClass.grade),
          class: Number(stampClass.class),
          isActive: Boolean(stampClass.isActive)
        }));
      }

      // stampImageUrl이 있는 경우 추가
      if (stampImageUrl) {
        updatedData.stampImageUrl = stampImageUrl;
      }

      // undefined 값 제거
      Object.keys(updatedData).forEach(key => {
        if (updatedData[key] === undefined) {
          delete updatedData[key];
        }
      });

      await updateDoc(teacherDocRef, updatedData);

      // 학기 날짜·포인트 등수: 반드시 이번에 저장한 담임 학급 classId 기준 (이전 teacher.classId 사용 시
      // 첫 담임 설정·학급 변경 시 잘못된 문서에 쓰이거나 저장이 생략되어 대시보드에 반영되지 않음)
      const effectiveClassId =
        homeroomClass.grade > 0 && homeroomClass.class > 0
          ? `${teacher.schoolId}_${homeroomClass.grade}_${homeroomClass.class}`
          : '';
      if (effectiveClassId) {
        try {
          const classRef = doc(db, `schools/${teacher.schoolId}/classes/${effectiveClassId}`);
          await setDoc(
            classRef,
            {
              semesterDates,
              pointRankingMode,
              updatedAt: serverTimestamp()
            },
            { merge: true }
          );
        } catch (dateError) {
          console.error('학기 날짜 저장 중 오류:', dateError);
        }
      }
      
      // 현재 사용자 정보 업데이트
      setCurrentUser((prev: User | null) => {
        if (!prev || prev.role !== 'teacher') return prev;
        return {
          ...prev,
          ...updatedData
        };
      });
      
      setSuccess('프로필이 성공적으로 업데이트되었습니다.');
    } catch (err) {
      console.error('프로필 업데이트 중 오류:', err);
      setError('프로필 업데이트에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('정말로 탈퇴하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }

    try {
      await deleteAccount();
      navigate('/');
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleSchoolChange = async () => {
    if (!teacher?.uid || !selectedSchoolForChange) return;
    if (selectedSchoolForChange === teacher.schoolId) {
      setSuccess('이미 선택한 학교에 소속되어 있습니다.');
      return;
    }

    const newSchool = schools.find(s => s.id === selectedSchoolForChange);
    if (!newSchool) return;

    setChangingSchool(true);
    setError(null);
    setSuccess(null);

    try {
      const newTeacherRef = doc(db, `schools/${selectedSchoolForChange}/teachers/${teacher.uid}`);
      const newTeacherDoc = await getDoc(newTeacherRef);

      if (newTeacherDoc.exists()) {
        // 이미 해당 학교에 소속되어 있으면 바로 전환
        const teacherData = newTeacherDoc.data();
        const teacherWithNewSchool: Teacher = {
          ...teacher,
          schoolId: selectedSchoolForChange,
          name: teacherData.name || teacher.name,
          email: teacherData.email || teacher.email,
          role: (teacherData.role || 'teacher') as Teacher['role'],
          homeroomClass: teacherData.homeroomClass,
          stampClasses: teacherData.stampClasses || [],
          classRoles: teacherData.classRoles || [],
          subject: teacherData.subject || '',
          subjectId: teacherData.subjectId || '',
          classId: teacherData.homeroomClass
            ? `${selectedSchoolForChange}_${teacherData.homeroomClass.grade}_${teacherData.homeroomClass.class}`
            : undefined,
          isActive: teacherData.isActive ?? true,
          createdAt: (teacherData.createdAt?.toDate?.() || new Date()) as Date,
          updatedAt: new Date()
        };

        localStorage.setItem(ACTIVE_SCHOOL_STORAGE_KEY, selectedSchoolForChange);
        setCurrentUser(teacherWithNewSchool);
        setSuccess(`소속교가 "${newSchool.name}"(으)로 변경되었습니다. 페이지를 새로고침합니다.`);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        // 해당 학교에 소속되어 있지 않으면 승인 요청
        const requestData = {
          uid: teacher.uid,
          userId: teacher.uid,
          email: teacher.email,
          name: teacher.name,
          role: 'teacher',
          schoolId: selectedSchoolForChange,
          schoolCode: newSchool.code,
          status: 'pending',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        await setDoc(doc(db, 'teacher_requests', teacher.uid), requestData);
        await upsertUsersRootProfile({
          uid: teacher.uid,
          email: teacher.email || '',
          name: teacher.name || '',
          role: 'teacher',
          schoolId: selectedSchoolForChange,
          loginType: teacher.loginType || 'local',
        });
        setSuccess(`"${newSchool.name}" 소속 요청이 제출되었습니다. 관리자 승인 후 소속교를 변경할 수 있습니다.`);
      }
    } catch (err) {
      console.error('소속교 변경 중 오류:', err);
      setError('소속교 변경 중 오류가 발생했습니다.');
    } finally {
      setChangingSchool(false);
    }
  };

  const handleHomeroomClassDelete = async () => {
    try {
      if (!teacher?.uid || !teacher?.schoolId) {
        setError('사용자 정보를 찾을 수 없습니다.');
        return;
      }

      if (!teacher.homeroomClass || !teacher.classId) {
        setError('담임 학급이 설정되어 있지 않습니다.');
        return;
      }

      // 해당 학급의 학생 수 확인
      const studentsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students`);
      const studentsSnapshot = await getDocs(studentsRef);
      const studentCount = studentsSnapshot.docs.length;

      // 학생이 있으면 경고 메시지 표시
      if (studentCount > 0) {
        const warningMessage = `이 학급에 ${studentCount}명의 학생이 있습니다. 담임을 해제해도 기존 학생들은 그대로 남아있습니다. 계속하시겠습니까?`;
        if (!window.confirm(warningMessage)) {
          return;
        }
      }

      setLoading(true);
      setError(null);

      const batch = writeBatch(db);
      
      // 교사 정보 업데이트 (올바른 경로 사용)
      const teacherRef = doc(db, `schools/${teacher.schoolId}/teachers/${teacher.uid}`);
      batch.update(teacherRef, {
        homeroomClass: deleteField(),
        classId: deleteField(),
        updatedAt: serverTimestamp()
      });
      
      await batch.commit();

      // 현재 사용자 정보 업데이트
      setCurrentUser((prev: User | null) => {
        if (!prev || prev.role !== 'teacher') return prev;
        const updated = { ...prev };
        delete (updated as any).homeroomClass;
        delete (updated as any).classId;
        return updated;
      });

      // 로컬 상태 업데이트
      setHomeroomClass({ grade: 0, class: 0 });
      setFormData(prev => ({
        ...prev,
        homeroomClass: undefined
      }));

      setSuccess('담임 학급이 성공적으로 해제되었습니다.');
    } catch (err) {
      setError('담임 학급을 제거하는 중 오류가 발생했습니다.');
      console.error('Error deleting homeroom class:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClassSelection = (grade: number, classNum: number) => {
    const key = `${grade}_${classNum}`;
    setSelectedClasses(prev => {
      const newSelected = { ...prev, [key]: !prev[key] };
      
      // stampClasses 상태 업데이트
      const newStampClasses = Object.entries(newSelected)
        .filter(([_, isSelected]) => isSelected)
        .map(([key]) => {
          const [grade, classNum] = key.split('_').map(Number);
          return { grade, class: classNum, isActive: true };
        });
      
      setStampClasses(newStampClasses);
      return newSelected;
    });
  };

  useEffect(() => {
    if (stampClasses) {
      const newSelected = stampClasses.reduce((acc, curr) => {
        acc[`${curr.grade}_${curr.class}`] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setSelectedClasses(newSelected);
    }
  }, [stampClasses]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontFamily: 'Pretendard, sans-serif'
      }}>
        <div style={{ width: 40, height: 40, border: '3px solid var(--Color-line-normal)', borderTopColor: 'var(--Color-fill-brand-color)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  const gradeOptions = getGradeRange().map(grade => ({
    value: grade.toString(),
    label: `${grade}학년`
  }));

  const classOptions = homeroomClass.grade > 0 
    ? getClassRange(homeroomClass.grade).map(classNum => ({
        value: classNum.toString(),
        label: `${classNum}반`
      }))
    : [];

  const subjectOptions = (Object.keys(SUBJECTS) as SubjectType[]).map(subjectName => ({
    value: subjectName,
    label: subjectName
  }));

  return (
    <div style={{
      width: '100%',
      minHeight: 'fit-content',
      display: 'flex',
      flexDirection: 'column',
      gap: pxToRem(32),
      fontFamily: 'Pretendard, sans-serif'
    }}>
      {/* 헤더 */}
      <div style={{
        alignSelf: 'stretch',
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        color: 'var(--Color-text-normal, #161616)',
        fontSize: pxToRem(32),
        fontFamily: 'Pretendard, sans-serif',
        fontWeight: '600',
        lineHeight: pxToRem(48),
        wordWrap: 'break-word'
      }}>
        설정
      </div>

      {error && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#ffebee', color: '#c62828', borderRadius: 8 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: 8 }}>
          {success}
        </div>
      )}

      {/* 탭 버튼 */}
      <div style={{
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: pxToRem(16),
        display: 'inline-flex',
        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
      }}>
        <div
          onClick={() => setActiveTab('profile')}
          style={{
            height: pxToRem(40),
            paddingLeft: pxToRem(12),
            paddingRight: pxToRem(12),
            paddingTop: pxToRem(8),
            paddingBottom: pxToRem(8),
            background: activeTab === 'profile' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
            borderRadius: pxToRem(8),
            justifyContent: 'center',
            alignItems: 'center',
            gap: pxToRem(4),
            display: 'flex',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginBottom: pxToRem(-1)
          }}
        >
          <div style={{
            color: activeTab === 'profile' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
            fontSize: pxToRem(14),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(21),
            wordWrap: 'break-word'
          }}>
            프로필 설정
          </div>
        </div>
        <div
          onClick={() => setActiveTab('homeroom')}
          style={{
            height: pxToRem(40),
            paddingLeft: pxToRem(12),
            paddingRight: pxToRem(12),
            paddingTop: pxToRem(8),
            paddingBottom: pxToRem(8),
            background: activeTab === 'homeroom' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
            borderRadius: pxToRem(8),
            justifyContent: 'center',
            alignItems: 'center',
            gap: pxToRem(4),
            display: 'flex',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginBottom: pxToRem(-1)
          }}
        >
          <div style={{
            color: activeTab === 'homeroom' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
            fontSize: pxToRem(14),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(21),
            wordWrap: 'break-word'
          }}>
            담임 설정
          </div>
        </div>
        {teacher?.subject && (
          <div
            onClick={() => setActiveTab('subject')}
            style={{
              height: pxToRem(40),
              paddingLeft: pxToRem(12),
              paddingRight: pxToRem(12),
              paddingTop: pxToRem(8),
              paddingBottom: pxToRem(8),
              background: activeTab === 'subject' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
              borderRadius: pxToRem(8),
              justifyContent: 'center',
              alignItems: 'center',
              gap: pxToRem(4),
              display: 'flex',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: pxToRem(-1)
            }}
          >
            <div style={{
              color: activeTab === 'subject' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(14),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: pxToRem(21),
              wordWrap: 'break-word'
            }}>
              교과 설정
            </div>
          </div>
        )}
        <div
          onClick={() => setActiveTab('stamp')}
          style={{
            height: pxToRem(40),
            paddingLeft: pxToRem(12),
            paddingRight: pxToRem(12),
            paddingTop: pxToRem(8),
            paddingBottom: pxToRem(8),
            background: activeTab === 'stamp' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
            borderRadius: pxToRem(8),
            justifyContent: 'center',
            alignItems: 'center',
            gap: pxToRem(4),
            display: 'flex',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginBottom: pxToRem(-1)
          }}
        >
          <div style={{
            color: activeTab === 'stamp' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
            fontSize: pxToRem(14),
            fontFamily: 'Pretendard, sans-serif',
            fontWeight: '500',
            lineHeight: pxToRem(21),
            wordWrap: 'break-word'
          }}>
            도장 설정
          </div>
        </div>
      </div>

      {/* 프로필 설정 탭 */}
      {activeTab === 'profile' && (
        <Card
          width="100%"
          padding={`${pxToRem(24)} ${pxToRem(28)}`}
          alignItems="flex-start"
          gap={pxToRem(24)}
        >
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: pxToRem(24)
          }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(18),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '600',
              lineHeight: pxToRem(27),
              wordWrap: 'break-word'
            }}>
              프로필 설정
            </div>
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(16)
            }}>
              <InputField
                value={formData.name}
                onChange={(val) => setFormData({ ...formData, name: val })}
                placeholder="이름을 입력하세요"
                width="100%"
                height={pxToRem(48)}
                size="large"
                type="text"
                label="이름"
              />
              <div style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: pxToRem(8)
              }}>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: pxToRem(14),
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: '500',
                  lineHeight: pxToRem(21)
                }}>
                  소속교
                </div>
                <div style={{
                  display: 'flex',
                  gap: pxToRem(12),
                  alignItems: 'flex-end',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ flex: 1, minWidth: pxToRem(200) }}>
                    <DropdownButton
                      options={schools.map(s => ({ value: s.id, label: s.name }))}
                      value={selectedSchoolForChange}
                      onChange={(val) => setSelectedSchoolForChange(val)}
                      placeholder={loadingSchools ? '학교 목록 로딩 중...' : '학교 선택'}
                      width="100%"
                      height={pxToRem(48)}
                      size="large"
                      disabled={loadingSchools}
                    />
                  </div>
                  <Button
                    variant="primary"
                    width={pxToRem(120)}
                    height={pxToRem(48)}
                    onClick={handleSchoolChange}
                    disabled={changingSchool || !selectedSchoolForChange || selectedSchoolForChange === teacher?.schoolId}
                  >
                    {changingSchool ? '처리 중...' : '소속교 변경'}
                  </Button>
                </div>
                {teacher?.schoolId && schoolInfo.name && (
                  <div style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: pxToRem(12),
                    opacity: 0.8
                  }}>
                    현재 소속: {schoolInfo.name}
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: pxToRem(4)
                  }}
                >
                  <span style={{
                    color: '#989898',
                    fontSize: pxToRem(12),
                    fontWeight: 500
                  }}>
                    학교가 목록에 없으신가요?
                  </span>
                  <a
                    href={SCHOOL_REGISTRATION_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'var(--Color-fill-brand-color, #FF6F0F)',
                      fontSize: pxToRem(12),
                      fontWeight: 500,
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    학교 등록 요청하기
                  </a>
                </div>
              </div>
              <Button
                variant="cancel"
                width={pxToRem(118)}
                height={pxToRem(40)}
                onClick={() => setShowDeleteConfirm(true)}
              >
                계정 삭제
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 담임 설정 탭 */}
      {activeTab === 'homeroom' && (
        <Card
          width="100%"
          padding={`${pxToRem(24)} ${pxToRem(28)}`}
          alignItems="flex-start"
          gap={pxToRem(24)}
        >
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: pxToRem(24)
          }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(18),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '600',
              lineHeight: pxToRem(27),
              wordWrap: 'break-word'
            }}>
              담임 설정
            </div>
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(16)
            }}>
              <div style={{
                width: '100%',
                display: 'flex',
                gap: pxToRem(16),
                alignItems: 'flex-start'
              }}>
                <div style={{ flex: 1 }}>
                  <DropdownButton
                    options={gradeOptions}
                    value={homeroomClass.grade > 0 ? homeroomClass.grade.toString() : ''}
                    onChange={(val) => handleHomeroomClassChange('grade', val)}
                    placeholder="학년 선택"
                    width="100%"
                    height={pxToRem(48)}
                    size="large"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <DropdownButton
                    options={classOptions}
                    value={homeroomClass.class > 0 ? homeroomClass.class.toString() : ''}
                    onChange={(val) => handleHomeroomClassChange('class', val)}
                    placeholder="반 선택"
                    width="100%"
                    height={pxToRem(48)}
                    size="large"
                    disabled={homeroomClass.grade === 0}
                  />
                </div>
                {teacher?.homeroomClass && (
                  <Button
                    variant="cancel"
                    width={pxToRem(118)}
                    height={pxToRem(48)}
                    onClick={handleHomeroomClassDelete}
                  >
                    담임 해제
                  </Button>
                )}
              </div>
              {teacher?.homeroomClass && (
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: pxToRem(24)
                }}>
                  <div style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: pxToRem(16)
                  }}>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: pxToRem(14),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '600',
                      lineHeight: pxToRem(21),
                      wordWrap: 'break-word'
                    }}>
                      학기 날짜 설정
                    </div>
                  <div style={{
                    width: '100%',
                    display: 'flex',
                    gap: pxToRem(16),
                    alignItems: 'flex-start'
                  }}>
                    <div style={{ flex: 1 }}>
                      <InputField
                        value={toDateInputValue(semesterDates.startDate)}
                        onChange={(val) => setSemesterDates(prev => ({ ...prev, startDate: val }))}
                        placeholder="시작일 선택"
                        width="100%"
                        height={pxToRem(48)}
                        size="large"
                        type="date"
                        label="시작일"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <InputField
                        value={toDateInputValue(semesterDates.endDate)}
                        onChange={(val) => setSemesterDates(prev => ({ ...prev, endDate: val }))}
                        placeholder="종료일 선택"
                        width="100%"
                        height={pxToRem(48)}
                        size="large"
                        type="date"
                        label="종료일"
                      />
                    </div>
                  </div>
                </div>
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: pxToRem(16)
                }}>
                  <div style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: pxToRem(14),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '600',
                    lineHeight: pxToRem(21),
                    wordWrap: 'break-word'
                  }}>
                    포인트 등수 기준
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: pxToRem(8)
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: pxToRem(8),
                      cursor: 'pointer',
                      fontSize: pxToRem(14),
                      color: 'var(--Color-text-normal, #161616)'
                    }}>
                      <input
                        type="radio"
                        name="pointRankingMode"
                        checked={pointRankingMode === 'balance'}
                        onChange={() => setPointRankingMode('balance')}
                        style={{ width: 16, height: 16, accentColor: 'var(--Color-fill-brand-color, #FF6F0F)' }}
                      />
                      보유 포인트 기준 (벌어든 포인트 − 사용한 포인트)
                    </label>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: pxToRem(8),
                      cursor: 'pointer',
                      fontSize: pxToRem(14),
                      color: 'var(--Color-text-normal, #161616)'
                    }}>
                      <input
                        type="radio"
                        name="pointRankingMode"
                        checked={pointRankingMode === 'totalEarned'}
                        onChange={() => setPointRankingMode('totalEarned')}
                        style={{ width: 16, height: 16, accentColor: 'var(--Color-fill-brand-color, #FF6F0F)' }}
                      />
                      총 벌어든 포인트만 (사용한 것은 제외)
                    </label>
                  </div>
                </div>
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: pxToRem(16)
                }}>
                  <ToggleSwitch
                    checked={formData.homeroomClass?.isWordGameActive || false}
                    disabled={!teacher?.homeroomClass}
                    onChange={(checked) => {
                      setFormData(prev => ({
                        ...prev,
                        homeroomClass: {
                          ...prev.homeroomClass!,
                          isWordGameActive: checked,
                          wordGameSettings: checked ? {
                            dailyWordLimit: prev.homeroomClass?.wordGameSettings?.dailyWordLimit || 10,
                            enablePoints: prev.homeroomClass?.wordGameSettings?.enablePoints || true,
                            dailyLimit: prev.homeroomClass?.wordGameSettings?.dailyLimit || true
                          } : undefined
                        }
                      }));
                    }}
                    label="오늘의 영단어"
                  />
                  {formData.homeroomClass?.isWordGameActive && (
                    <div style={{
                      paddingLeft: pxToRem(24),
                      display: 'flex',
                      flexDirection: 'column',
                      gap: pxToRem(16)
                    }}>
                      <div style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: pxToRem(14),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '600',
                        lineHeight: pxToRem(21),
                        wordWrap: 'break-word'
                      }}>
                        단어 관리
                      </div>
                      <Button
                        variant="outline"
                        width={pxToRem(200)}
                        height={pxToRem(32)}
                        size="small"
                        onClick={() => navigate('/class/teacher/wordgame')}
                      >
                        단어 추가/수정/삭제
                      </Button>
                      <div style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: pxToRem(14),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '600',
                        lineHeight: pxToRem(21),
                        wordWrap: 'break-word'
                      }}>
                        게임 설정
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: pxToRem(16)
                      }}>
                        <InputField
                          value={(formData.homeroomClass?.wordGameSettings?.dailyWordLimit || 10).toString()}
                          onChange={(val) => {
                            const value = Math.max(1, Math.min(50, parseInt(val) || 10));
                            setFormData(prev => ({
                              ...prev,
                              homeroomClass: {
                                ...prev.homeroomClass!,
                                wordGameSettings: {
                                  ...prev.homeroomClass?.wordGameSettings!,
                                  dailyWordLimit: value
                                }
                              }
                            }));
                          }}
                          placeholder="하루 암기 단어 수"
                          width={pxToRem(200)}
                          height={pxToRem(48)}
                          size="large"
                          type="number"
                        />
                        <ToggleSwitch
                          checked={formData.homeroomClass?.wordGameSettings?.enablePoints || false}
                          onChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              homeroomClass: {
                                ...prev.homeroomClass!,
                                wordGameSettings: {
                                  ...prev.homeroomClass?.wordGameSettings!,
                                  enablePoints: checked
                                }
                              }
                            }));
                          }}
                          label="포인트 지급"
                        />
                        <ToggleSwitch
                          checked={formData.homeroomClass?.wordGameSettings?.dailyLimit || false}
                          onChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              homeroomClass: {
                                ...prev.homeroomClass!,
                                wordGameSettings: {
                                  ...prev.homeroomClass?.wordGameSettings!,
                                  dailyLimit: checked
                                }
                              }
                            }));
                          }}
                          label="하루 한 번 제한"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* 교과 설정 탭 */}
      {activeTab === 'subject' && teacher?.subject && (
        <Card
          width="100%"
          padding={`${pxToRem(24)} ${pxToRem(28)}`}
          alignItems="flex-start"
          gap={pxToRem(24)}
        >
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: pxToRem(24)
          }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(18),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '600',
              lineHeight: pxToRem(27),
              wordWrap: 'break-word'
            }}>
              교과 설정
            </div>
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(16)
            }}>
              <DropdownButton
                options={subjectOptions}
                value={subject}
                onChange={(val) => {
                  const selectedSubject = val as SubjectType;
                  setSubject(selectedSubject);
                  const subjectId = getSubjectId(selectedSubject);
                  setFormData(prev => ({
                    ...prev,
                    subject: subjectId,
                    subjectId: subjectId
                  }));
                }}
                placeholder="교과 선택"
                width="100%"
                height={pxToRem(48)}
                size="large"
              />
            </div>
          </div>
        </Card>
      )}

      {/* 도장 설정 탭 */}
      {activeTab === 'stamp' && (
        <Card
          width="100%"
          padding={`${pxToRem(24)} ${pxToRem(28)}`}
          alignItems="flex-start"
          gap={pxToRem(24)}
        >
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: pxToRem(24)
          }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(18),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '600',
              lineHeight: pxToRem(27),
              wordWrap: 'break-word'
            }}>
              도장 설정
            </div>
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(24)
            }}>
              <div style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: pxToRem(16)
              }}>
                {stampImageUrl && (
                  <img 
                    src={stampImageUrl}
                    alt="도장 이미지"
                    style={{
                      width: pxToRem(56),
                      height: pxToRem(56),
                      objectFit: 'cover',
                      borderRadius: pxToRem(8)
                    }}
                  />
                )}
                <Button
                  variant="submit"
                  width={pxToRem(200)}
                  height={pxToRem(48)}
                  disabled={uploading}
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) handleStampImageUpload(file);
                    };
                    input.click();
                  }}
                >
                  {uploading ? '업로드 중...' : '도장 이미지 업로드'}
                </Button>
              </div>
              <div style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: pxToRem(16)
              }}>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: pxToRem(18),
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: '600',
                  lineHeight: pxToRem(27),
                  wordWrap: 'break-word'
                }}>
                  도장 사용 학급 설정
                </div>
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: pxToRem(16)
                }}>
                  {getGradeRange().map(grade => (
                    <div key={grade} style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: pxToRem(8)
                    }}>
                      <div style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: pxToRem(14),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '500',
                        lineHeight: pxToRem(21),
                        wordWrap: 'break-word'
                      }}>
                        {grade}학년
                      </div>
                      <div style={{
                        width: '100%',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: pxToRem(8)
                      }}>
                        {getClassRange(grade).map(classNum => (
                          <div
                            key={`${grade}_${classNum}`}
                            onClick={() => handleClassSelection(grade, classNum)}
                            style={{
                              padding: `${pxToRem(8)} ${pxToRem(12)}`,
                              borderRadius: pxToRem(8),
                              border: `1px solid ${selectedClasses[`${grade}_${classNum}`] ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-line-normal, #F0F0F0)'}`,
                              background: selectedClasses[`${grade}_${classNum}`] ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                              color: selectedClasses[`${grade}_${classNum}`] ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                              fontSize: pxToRem(13),
                              fontFamily: 'Pretendard, sans-serif',
                              fontWeight: '500',
                              lineHeight: pxToRem(19.5),
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {classNum}반
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* 저장 버튼 */}
      <div style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: pxToRem(24)
      }}>
        <Button
          variant="submit"
          width={pxToRem(118)}
          height={pxToRem(48)}
          onClick={(e) => handleSubmit(e)}
          disabled={loading}
        >
          {loading ? '저장 중...' : '저장'}
        </Button>
      </div>

      {/* 계정 삭제 확인 다이얼로그 */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <Card
            width={pxToRem(400)}
            padding={pxToRem(24)}
            alignItems="center"
            gap={pxToRem(20)}
          >
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(16),
              textAlign: 'center'
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: pxToRem(18),
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: '600',
                lineHeight: pxToRem(27),
                wordWrap: 'break-word'
              }}>
                계정을 삭제하시겠습니까?
              </div>
              <div style={{
                color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                fontSize: pxToRem(14),
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: '400',
                lineHeight: pxToRem(21),
                wordWrap: 'break-word'
              }}>
                이 작업은 되돌릴 수 없습니다.
              </div>
              <div style={{
                width: '100%',
                display: 'flex',
                gap: pxToRem(12),
                justifyContent: 'center'
              }}>
                <Button
                  variant="cancel"
                  width={pxToRem(118)}
                  height={pxToRem(48)}
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  취소
                </Button>
                <Button
                  variant="submit"
                  width={pxToRem(118)}
                  height={pxToRem(48)}
                  onClick={handleDeleteAccount}
                >
                  삭제
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

    </div>
  );
};

export default TeacherSetting;